import { InputHTMLAttributes, forwardRef, ReactNode } from 'react'
import { cn } from '../../utils/cn'

interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string | ReactNode
  error?: string
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, error, checked, disabled, children, ...props }, ref) => {
    return (
      <div className="flex items-start space-x-3">
        <div className="relative">
          <input
            ref={ref}
            type="checkbox"
            className={cn(
              'w-5 h-5 rounded border transition-all duration-200 ease-in-out',
              'focus:outline-none focus:ring-2 focus:ring-olr-primary focus:ring-offset-2',
              'cursor-pointer',
              !error && !disabled && 'border-gray-300 text-olr-primary',
              error && 'border-red-500',
              disabled && 'border-gray-200 bg-gray-50 cursor-not-allowed',
              className
            )}
            checked={checked}
            disabled={disabled}
            {...props}
          />
          {checked && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <svg
                className="w-3 h-3 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>

        {(label || children) && (
          <div className="flex-1">
            <label
              className={cn(
                'text-sm font-medium cursor-pointer',
                !error && !disabled && 'text-gray-700',
                error && 'text-red-600',
                disabled && 'text-gray-500 cursor-not-allowed'
              )}
            >
              {label || children}
            </label>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        )}
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'
